import React, { memo, useState } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import "./RegistrationScreen.css";
import { withTranslation, WithTranslation } from "react-i18next";

import LoggedOutLayout from "../components/LoggedOutLayout/LoggedOutLayout";
import {
  CopyrightText,
  Divider,
  FooterSection,
  FooterText,
} from "../components/LoggedOutLayout/LoggedOutLayout.elements";

import ConfirmRegistrationStep from "../components/ConfirmRegistrationStep";
import CompanyDataStep from "../components/CompanyDataStep/CompanyDataStep";
import RecommendationStep from "../components/RecommendationStep/RecommendationStep";
import SuccessRegistrationStep from "../components/SuccessRegistrationStep/SuccessRegistrationStep";
import { useHistory } from "react-router-dom";
import RegistrationStartStep, { AccountType } from "../components/RegistrationStartStep/RegistrationStartStep";
import PersonalRegistrationStep from "../components/PersonalRegistrationStep/PersonalRegistrationStep";
import CompanyEmailStep from "../components/CompanyEmailStep/CompanyEmailStep";

export function RegistrationScreen({ t }: WithTranslation) {
  const history = useHistory();
  const [step, setStep] = useState<
    | "start"
    | "personal"
    | "business"
    | "confirm"
    | "companyData"
    | "recommendation"
    | "success"
  >("start");
  const [registrationData, setRegistrationData] = useState({
    email: "",
    country: "",
    companyName: "",
    regNumber: "",
    taxNumber: "",
    accepted: false,
    referralCode: "",
    joined: false,
    confirmationCode: ""
  });

  const handleNext = (accountType: AccountType) => {
    if (accountType === "individual") {
      setStep("personal");
    }
    if (accountType === "business") {
      setStep("business");
    }
  };

  const handleBusinessNext = () => {
    setStep("confirm");
  };

  const handleConfirmNext = (confirmationCode: string) => {
    setRegistrationData((prev) => ({ ...prev, confirmationCode }));
    setStep("companyData");
  };

  const handleCompanyDataFinish = (data: any) => {
    setRegistrationData((prev) => ({ ...prev, ...data }));
    setStep("recommendation");
  };

  const handleRecommendationFinish = (data: any) => {
    setRegistrationData((prev) => ({
      ...prev,
      referralCode: data.referralCode || "",
      joined: !!data.joined
    }));
    setStep("success");
  };

  const hasBackButton =
    step !== "start" &&
    step !== "success" &&
    step !== "recommendation" &&
    step !== "companyData";

  const handleBack = () => {
    if (step === "recommendation") {
      setStep("companyData");
    } else if (step === "companyData") {
      setStep("confirm");
    } else if (step === "confirm") {
      setStep("business");
    } else if (step === "business") {
      setStep("start");
    } else if (step === "personal") {
      setStep("start");
    } else if (step === "success") {
      history.push("/");
    }
    else {
      setStep("start");
    }
  };

  return (
    <>
      <LoggedOutLayout onBack={hasBackButton ? handleBack : null}>
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={step}
            timeout={300}
            classNames="fade"
            style={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div>
              {step === "start" && (
                <RegistrationStartStep
                  onNext={handleNext}
                  onBack={() => history.push("/")}
                />
              )}
              {step === "personal" && <PersonalRegistrationStep />}
              {step === "business" && (
                <CompanyEmailStep
                  email={registrationData.email}
                  country={registrationData.country}
                  setEmail={(email: string) => setRegistrationData((prev) => ({ ...prev, email }))}
                  setCountry={(country: string) => setRegistrationData((prev) => ({ ...prev, country }))}
                  onNext={handleBusinessNext}
                />
              )}
              {step === "confirm" && (
                <ConfirmRegistrationStep
                  email={registrationData.email}
                  country={registrationData.country}
                  onEditEmail={() => setStep("business")}
                  onNext={handleConfirmNext}
                />
              )}
              {step === "companyData" && (
                <CompanyDataStep
                  companyName={registrationData.companyName}
                  regNumber={registrationData.regNumber}
                  taxNumber={registrationData.taxNumber}
                  accepted={registrationData.accepted}
                  setCompanyName={(companyName: string) => setRegistrationData((prev) => ({...prev, companyName}))}
                  setRegNumber={(regNumber: string) => setRegistrationData((prev) => ({...prev, regNumber}))}
                  setTaxNumber={(taxNumber: string) => setRegistrationData((prev) => ({...prev, taxNumber}))}
                  setAccepted={(accepted: boolean) => setRegistrationData((prev) => ({...prev, accepted}))}
                  onFinish={handleCompanyDataFinish}
                />
              )}
              {step === "recommendation" && (
                <RecommendationStep
                  referralCode={registrationData.referralCode}
                  setReferralCode={(referralCode: string) => setRegistrationData((prev) => ({ ...prev, referralCode }))}
                  joined={registrationData.joined}
                  setJoined={(joined: boolean) => setRegistrationData((prev) => ({ ...prev, joined }))}
                  email={registrationData.email}
                  companyName={registrationData.companyName}
                  regNumber={registrationData.regNumber}
                  taxNumber={registrationData.taxNumber}
                  confirmationCode={registrationData.confirmationCode}
                  onFinish={handleRecommendationFinish}
                />
              )}
              {step === "success" && <SuccessRegistrationStep email={registrationData.email} onBack={handleBack}/>} 
              <FooterSection>
                <FooterText>{t("registration.footerInfo")}</FooterText>
                <Divider />
                <CopyrightText>{t("registration.copyright")}</CopyrightText>
              </FooterSection>
            </div>
          </CSSTransition>
        </SwitchTransition>
      </LoggedOutLayout>
    </>
  );
}

export default withTranslation()(memo(RegistrationScreen));
